import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HeadlineLarge, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import TextField from '@shared/ui/atoms/TextField';
import AccessRequired from '../components/AccessRequired';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { SecondaryButton } from '@shared/ui/atoms/buttons/SecondaryButton';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { canManageWorkGroups } from '@shared/utils/AccessControl';
import { Column } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { useTranslation } from 'react-i18next';
import { loadConfig } from '@shared/store/config.duck';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { fetchTags, selectAllTags } from '@shared/store/userTags.duck';
import { deleteTagRequest, getAllUsersInTag, updateTagRequest } from '@shared/services/Tags';
import { BasicUserInfo } from '@shared/models/user.models';
import { TagWithCount } from '@shared/models/tags.models';
import { WorkGroupMemberRow } from './components/WorkGroupMemberRow';

interface Props {
  workGroupId: number;
  onClose: () => void;
}

export function EditWorkGroupView({ workGroupId, onClose }: Props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const workGroups = useSelector(selectAllTags);
  const canManage = useMemo(() => canManageWorkGroups(currentUser), [currentUser]);
  const { t } = useTranslation();

  const workGroup = useMemo(
    () => workGroups.find((wg: TagWithCount) => wg.id === workGroupId),
    [workGroups, workGroupId],
  );

  const [name, setName] = useState('');
  const [members, setMembers] = useState<BasicUserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (workGroup) {
      setName(workGroup.label);
    }
  }, [workGroup]);

  useEffect(() => {
    // Workgroups come from the tags store; if they haven't loaded yet, fetch
    // them. If they have loaded but this id isn't among them, the link is stale.
    if (!workGroup) {
      if (workGroups.length === 0) {
        dispatch(fetchTags());
      } else {
        onClose();
      }
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAllUsersInTag(workGroup.id)
      .then(users => { if (!cancelled) setMembers(users); })
      .catch(() => { if (!cancelled) setMembers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workGroup, workGroups.length, dispatch, reloadToken]);

  const saveChanges = async () => {
    if (!workGroup) return;
    setLoading(true);
    try {
      await updateTagRequest({ ...workGroup, label: name });
      dispatch(loadConfig(['workGroups']));
      dispatch(successNotification('Workgroup updated'));
      onClose();
    } catch {
      dispatch(failureNotification('Workgroup failed to update'));
      setLoading(false);
    }
  };

  const onMemberRemoved = (userId: string) => {
    setMembers(prev => prev.filter(u => u.userId !== userId));
    // Keep workgroup member counts in sync on the list view.
    dispatch(loadConfig(['workGroups']));
  };

  const openAddMembers = () => {
    if (!workGroup) return;
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.EditUsersInTagDialog,
      payload: {
        tag: workGroup,
        // Refresh the member list once the dialog closes with changes.
        onClose: () => setReloadToken(token => token + 1),
      },
    }));
  };

  const onDelete = async () => {
    if (!workGroup) return;
    setLoading(true);
    try {
      await deleteTagRequest(workGroup.id);
      dispatch(loadConfig(['workGroups']));
      dispatch(successNotification('Workgroup deleted'));
      onClose();
    } catch {
      dispatch(failureNotification('Failed to delete workgroup'));
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      {loading && <LoadingSpinner fullScreen={true} />}
      <div className="management">

        <GoBackButton onClick={onClose} />
        <HeadlineLarge className="management__title">{t('edit-workgroup.edit-workgroup')}</HeadlineLarge>
        <AccessRequired visible={!canManage} teamAdminsRequired={true} companyAdminsRequired={true} />

        <Column className="management__row">
          <TextField dataTest="editWorkGroupView-name-input"
                     label={'edit-workgroup.edit-workgroup-name'}
                     value={name}
                     onChange={setName}
                     disabled={!canManage} />

          {canManage &&
            <SecondaryButton dataTest="editWorkGroupView-save"
                             size={ButtonSize.Large}
                             text="button.save-changes"
                             fullWidth={false}
                             click={saveChanges} />}
        </Column>

        <div className="editTeam__membersHeader">
          <HeadlineSmall className="management__row">{t('members')}</HeadlineSmall>
          {canManage &&
            <OutlineButton dataTest="editWorkGroupView-addMembers"
                           className="management__row"
                           size={ButtonSize.Medium}
                           text="button.add-members"
                           click={openAddMembers} />}
        </div>
        {members.map(user => (
          <WorkGroupMemberRow key={user.userId}
                              user={user}
                              workGroupId={workGroupId}
                              canRemove={canManage}
                              onRemoved={onMemberRemoved} />
        ))}

        {canManage &&
          <div className="management__loneControl">
            <IconButton color="darkGrey"
                        icon={IconTypes.Bin}
                        text="button.delete"
                        onClick={onDelete} />
          </div>}

      </div>
    </React.Fragment>
  );
}
